import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import { useSEO } from '../contexts/SEOContext';
import { useDomainManager } from '../config';
import { handleImageErrorWithFallback } from '../utils/imageManager';
import { renderCtaButton } from '../utils/ctaHelper';

const Products = () => {
  const navigate = useNavigate();
  const { brand, domainConfig, cta } = useTheme();
  const { variant } = useVariant();
  const { currentSEO } = useSEO();
  const domainManager = useDomainManager(brand?.id);
  
  // State for category filtering
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Get product data from domain config
  const getProductsData = () => {
    if (domainConfig?.products) {
      return {
        title: domainConfig.products.title || 'Our Products',
        subtitle: domainConfig.products.subtitle || 'Discover our comprehensive range of products.',
        categories: domainConfig.products.categories || [],
        products: domainConfig.products.items || []
      };
    }
    
    // Fallback if no products configured
    return {
      title: 'Our Products',
      subtitle: 'Discover our comprehensive range of products.',
      categories: [{ id: 'all', name: 'All Products' }],
      products: []
    };
  };

  const productsData = getProductsData();
  const { title: pageTitle, subtitle: pageSubtitle, categories, products } = productsData;

  // Filter products based on selected category
  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(product => product.category === selectedCategory));
    }
  }, [selectedCategory, products]);

  // Check if products are configured for this domain
  if (!domainConfig?.products || !products.length) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Products Not Available</h1>
          <p className="text-gray-600">Products are not configured for this domain.</p>
        </div>
      </div>
    );
  }

  // Generate image path for products
  const getProductImagePath = (product) => {
    if (product.image) {
      return `/assets/domains/${brand.id}/${product.image}.jpg`;
    }
    return `/assets/domains/${brand.id}/product-${product.id}.jpg`;
  };

  // Get domain-specific styling based on variant
  const getVariantStyles = () => {
    switch (variant) {
      case 'minimal':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 hover:shadow-lg transition-shadow duration-300',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          categoryButton: 'text-gray-600 hover:text-gray-900 border-gray-200 hover:border-gray-400',
          categoryButtonActive: 'text-gray-900 border-gray-900 bg-gray-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-600',
          productTitle: 'text-gray-900',
          productDescription: 'text-gray-600',
          noProductsText: 'text-gray-500',
          sectionTitle: 'text-gray-900',
          sectionSubtitle: 'text-gray-600',
          sectionBg: 'bg-gray-50',
          featuredBadge: 'bg-gray-800 text-white'
        };
      case 'modern':
        return {
          container: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1',
          button: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600',
          buttonSecondary: 'border-2 border-white/30 text-white hover:bg-white/10 hover:text-slate-900',
          categoryButton: 'text-purple-200 hover:text-white border-white/20 hover:border-white/40',
          categoryButtonActive: 'text-white border-white bg-white/10',
          title: 'text-white',
          subtitle: 'text-purple-100',
          productTitle: 'text-white',
          productDescription: 'text-purple-100',
          noProductsText: 'text-purple-200',
          sectionTitle: 'text-white',
          sectionSubtitle: 'text-purple-100',
          sectionBg: 'bg-gray-800/50',
          featuredBadge: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
        };
      case 'clean':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-100 hover:shadow-lg transition-all duration-300',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-800',
          categoryButton: 'text-gray-600 hover:text-gray-800 border-gray-200 hover:border-gray-300',
          categoryButtonActive: 'text-gray-800 border-gray-800 bg-gray-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-500',
          productTitle: 'text-gray-900',
          productDescription: 'text-gray-600',
          noProductsText: 'text-gray-500',
          sectionTitle: 'text-gray-900',
          sectionSubtitle: 'text-gray-500',
          sectionBg: 'bg-gray-50',
          featuredBadge: 'bg-gray-900 text-white'
        };
      case 'corporate':
        return {
          container: 'bg-gray-50',
          card: 'bg-white shadow-md hover:shadow-lg transition-shadow duration-300',
          button: 'bg-indigo-600 text-white hover:bg-indigo-700',
          buttonSecondary: 'border border-indigo-200 text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50',
          categoryButton: 'text-gray-600 hover:text-indigo-600 border-gray-200 hover:border-indigo-200',
          categoryButtonActive: 'text-indigo-600 border-indigo-600 bg-indigo-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-600',
          productTitle: 'text-gray-900',
          productDescription: 'text-gray-600',
          noProductsText: 'text-gray-500',
          sectionTitle: 'text-gray-900',
          sectionSubtitle: 'text-gray-600',
          sectionBg: 'bg-indigo-50',
          featuredBadge: 'bg-indigo-600 text-white'
        };
      case 'landing':
        return {
          container: 'bg-gradient-to-br from-blue-50 to-indigo-100',
          card: 'bg-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2',
          button: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700',
          buttonSecondary: 'border border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50',
          categoryButton: 'text-gray-600 hover:text-blue-600 border-gray-300 hover:border-blue-300',
          categoryButtonActive: 'text-blue-600 border-blue-600 bg-blue-100',
          title: 'text-gray-900',
          subtitle: 'text-gray-600',
          productTitle: 'text-gray-900',
          productDescription: 'text-gray-600',
          noProductsText: 'text-gray-500',
          sectionTitle: 'text-gray-900',
          sectionSubtitle: 'text-gray-600',
          sectionBg: 'bg-gradient-to-br from-blue-100 to-indigo-200',
          featuredBadge: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
        };
      case 'grid':
        return {
          container: 'bg-white',
          card: 'bg-white border-2 border-gray-100 hover:border-gray-300 hover:shadow-lg transition-all duration-300',
          button: 'bg-green-600 text-white hover:bg-green-700',
          buttonSecondary: 'border border-green-200 text-green-600 hover:border-green-300 hover:bg-green-50',
          categoryButton: 'text-gray-600 hover:text-green-600 border-gray-200 hover:border-green-200',
          categoryButtonActive: 'text-green-600 border-green-600 bg-green-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-600',
          productTitle: 'text-gray-900',
          productDescription: 'text-gray-600',
          noProductsText: 'text-gray-500',
          sectionTitle: 'text-gray-900',
          sectionSubtitle: 'text-gray-600',
          sectionBg: 'bg-green-50',
          featuredBadge: 'bg-green-600 text-white'
        };
      case 'creative':
        return {
          container: 'bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100',
          card: 'bg-white/80 backdrop-blur-sm border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1',
          button: 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600',
          buttonSecondary: 'border border-orange-300 text-orange-600 hover:border-orange-400 hover:bg-orange-50',
          categoryButton: 'text-gray-600 hover:text-orange-600 border-gray-200 hover:border-orange-200',
          categoryButtonActive: 'text-orange-600 border-orange-600 bg-orange-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-700',
          productTitle: 'text-gray-900',
          productDescription: 'text-gray-700',
          noProductsText: 'text-gray-500',
          sectionTitle: 'text-gray-900',
          sectionSubtitle: 'text-gray-700',
          sectionBg: 'bg-gradient-to-br from-orange-50 to-pink-50',
          featuredBadge: 'bg-gradient-to-r from-orange-500 to-pink-500 text-white'
        };
      case 'magazine':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          categoryButton: 'text-gray-600 hover:text-gray-800 border-gray-200 hover:border-gray-300',
          categoryButtonActive: 'text-gray-800 border-gray-800 bg-gray-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-600',
          productTitle: 'text-gray-900',
          productDescription: 'text-gray-600',
          noProductsText: 'text-gray-500',
          sectionTitle: 'text-gray-900',
          sectionSubtitle: 'text-gray-600',
          sectionBg: 'bg-gray-50',
          featuredBadge: 'bg-gray-800 text-white'
        };
      case 'professional':
        return {
          container: 'bg-slate-50',
          card: 'bg-white border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50',
          categoryButton: 'text-slate-600 hover:text-blue-600 border-slate-200 hover:border-blue-200',
          categoryButtonActive: 'text-blue-600 border-blue-600 bg-blue-50',
          title: 'text-slate-900',
          subtitle: 'text-slate-600',
          productTitle: 'text-slate-900',
          productDescription: 'text-slate-600',
          noProductsText: 'text-slate-500',
          sectionTitle: 'text-slate-900',
          sectionSubtitle: 'text-slate-600',
          sectionBg: 'bg-white',
          featuredBadge: 'bg-blue-600 text-white'
        };
      case 'elegant':
        return {
          container: 'bg-gradient-to-br from-blue-50 via-white to-blue-100',
          card: 'bg-white/80 backdrop-blur-sm border border-blue-200/50 shadow-sm hover:shadow-lg transition-all duration-300',
          button: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800',
          buttonSecondary: 'border border-blue-300 text-blue-700 hover:border-blue-400 hover:bg-blue-50',
          categoryButton: 'text-blue-600 hover:text-blue-800 border-blue-200 hover:border-blue-300',
          categoryButtonActive: 'text-blue-800 border-blue-800 bg-blue-50',
          title: 'text-blue-900',
          subtitle: 'text-blue-600',
          productTitle: 'text-blue-900',
          productDescription: 'text-blue-600',
          noProductsText: 'text-blue-500',
          sectionTitle: 'text-blue-900',
          sectionSubtitle: 'text-blue-600',
          sectionBg: 'bg-white/50',
          featuredBadge: 'bg-blue-800 text-white'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 hover:shadow-lg transition-all duration-300',
          button: 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700',
          buttonSecondary: 'border border-white/30 text-white hover:bg-white/10 hover:text-gray-900',
          categoryButton: 'text-gray-300 hover:text-white border-white/20 hover:border-white/40',
          categoryButtonActive: 'text-white border-white bg-white/10',
          title: 'text-white',
          subtitle: 'text-gray-300',
          productTitle: 'text-white',
          productDescription: 'text-gray-300',
          noProductsText: 'text-gray-400',
          sectionTitle: 'text-white',
          sectionSubtitle: 'text-gray-300',
          sectionBg: 'bg-gray-800/50',
          featuredBadge: 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
        };
      default:
        return {
          container: 'bg-white',
          card: 'bg-white shadow-md hover:shadow-lg transition-shadow duration-300',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-blue-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50',
          categoryButton: 'text-gray-600 hover:text-blue-600 border-gray-200 hover:border-blue-200',
          categoryButtonActive: 'text-blue-600 border-blue-600 bg-blue-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-600',
          productTitle: 'text-gray-900',
          productDescription: 'text-gray-600',
          noProductsText: 'text-gray-500',
          sectionTitle: 'text-gray-900',
          sectionSubtitle: 'text-gray-600',
          sectionBg: 'bg-blue-50',
          featuredBadge: 'bg-blue-600 text-white'
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div className={`min-h-screen ${styles.container}`}>
      {/* Header Section */}
      <section className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className={`text-4xl lg:text-5xl font-bold mb-4 ${styles.title}`}>
              {pageTitle}
            </h1>
            <p className={`text-xl max-w-3xl mx-auto ${styles.subtitle}`}>
              {pageSubtitle}
            </p>
          </div>

          {/* CTA Button - Top */}
          <div className="text-center mb-12">
            {renderCtaButton(cta?.secondary, 'Buy Now', `inline-flex items-center px-8 py-4 text-lg font-semibold rounded-lg transition-colors duration-200 ${styles.button}`, domainConfig?.shopLink)}
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full border-2 font-medium transition-all duration-200 ${
                  selectedCategory === category.id
                    ? styles.categoryButtonActive
                    : styles.categoryButton
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <div key={product.id} className={`rounded-xl p-6 ${styles.card}`}>
                {/* Product Image */}
                <div className="relative mb-6">
                  <img
                    src={getProductImagePath(product)}
                    alt={product.title}
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, `product-${product.id}`)}
                  />
                  {product.featured && (
                    <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${styles.featuredBadge}`}>
                      Featured
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="space-y-4">
                  <h3 className={`text-xl font-semibold ${styles.productTitle}`}>
                    {product.title}
                  </h3>
                  <p className={`leading-relaxed ${styles.productDescription}`}>
                    {product.description}
                  </p>
                  <div className="flex justify-end">
                    {product.details && product.details.length > 0 ? (
                      <button
                        onClick={() => navigate(`/products/${product.id}`)}
                        className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors duration-200 ${styles.buttonSecondary}`}
                      >
                        More Details
                      </button>
                    ) 
                    : 
                    (
                      <a
                        href={product.productLink || domainConfig?.shopLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors duration-200 ${styles.buttonSecondary}`}
                      >
                        More Details
                      </a>
                    )
                    }
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No Products Message */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className={`text-lg ${styles.noProductsText}`}>No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* View More Section */}
      <section className={`py-16 ${styles.sectionBg}`}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className={`text-3xl font-bold mb-4 ${styles.sectionTitle}`}>
            Need More Options?
          </h2>
          <p className={`text-xl mb-8 max-w-2xl mx-auto ${styles.sectionSubtitle}`}>
            Explore our complete catalog of fleet management solutions and find the perfect fit for your business needs.
          </p>
          {brand?.id === 'fleet-dashcam' ? (
            <a
              href="/compare-prices"
              className={`inline-flex items-center px-8 py-4 text-lg font-semibold rounded-lg transition-colors duration-200 ${styles.buttonSecondary}`}
            >
              View More Products
            </a>
          ) : (
            <a
              href={domainConfig?.shopLink || "https://shop.fleethoster.com/"}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center px-8 py-4 text-lg font-semibold rounded-lg transition-colors duration-200 ${styles.buttonSecondary}`}
            >
              View More Products
            </a>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
